import { Mutex, tryAcquire } from 'async-mutex'
import { ReactNode, useEffect, useRef, useState, useCallback } from 'react'
import Styles from './InfiniteScroll.module.scss'
import { Skeleton } from 'antd'
import { Spinner } from 'common/Spinner/Spinner'
import { ClassValue } from 'classnames/types'
import cx from 'classnames'

export enum IndicatorType {
  Skeleton = 'skeleton',
  Spin = 'spin',
}

const Indicators = {
  [IndicatorType.Skeleton]: Skeleton,
  [IndicatorType.Spin]: Spinner,
}

interface Props {
  hasMore: boolean
  indicatorType?: IndicatorType
  loadMore: () => void
  children?: ReactNode
  className?: ClassValue
}

const InfiniteScroll = ({ hasMore, indicatorType = IndicatorType.Spin, loadMore, children, className }: Props) => {
  const [loadMoreNode, setLoadMoreNode] = useState<HTMLDivElement | null>(null)
  const [currentObserver, setCurrentObserver] = useState<IntersectionObserver | null>(null)

  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return

    setLoadMoreNode(node)
  }, [])

  const loadMutex = useRef(new Mutex())

  useEffect(() => {
    if (currentObserver) {
      currentObserver.disconnect()
    }

    if (!loadMoreNode) {
      return
    }

    // NOTE: only load more if the spinner is coming INTO view
    const observer = new IntersectionObserver(([loadMoreEntry]) => {
      tryAcquire(loadMutex.current)
        .runExclusive(async () => {
          if (loadMoreEntry.isIntersecting) loadMore()
        })
        .catch(() => null) // fail silently if the mutex cannot be acquired
    })

    observer.observe(loadMoreNode)

    setCurrentObserver(observer)

    return () => observer.disconnect()
  }, [loadMore, loadMoreNode]) // eslint-disable-line react-hooks/exhaustive-deps

  const Indicator = Indicators[indicatorType]

  return (
    <>
      {hasMore && (
        <div ref={loadMoreRef} className={cx(children ? undefined : Styles.loadMore, className)}>
          {children ? children : <Indicator />}
        </div>
      )}
    </>
  )
}

export default InfiniteScroll
